import { IkeToken, type LocationDataRaw } from '@ikegps/core-api';
import * as IkeModel from '@ikegps/core-shared';
import { IkeUnit } from '@ikegps/core-shared';
import { LocationInput } from '../../../components/input/location.input';
import { LocationInputState } from '../../../components/input/location.input.state';
import { addStyles } from '../../../components/theme';
import { T, Translate } from '../../../i18n';
import { m, type MithrilVNode } from '../../../mithril';
import { Services } from '../../../services';
import { FieldInfo } from '../../field.info/field.info';
import { CollectionEditCtrl } from '../collection.view.ctrl';
import type { CollectionFieldView } from './field.view';

export const CSS = {
	container: 'c-Location',
	table: 'c-Location__table',
	row: 'c-LocationField',
	label: 'c-LocationField__label',
	value: 'c-LocationField__value',
	textfield: 'c-LocationField__text',
};

addStyles({
	[CSS.container]: {
		display: 'block',
		'align-items': 'center',
	},
	[CSS.table]: {
		width: '100%',
		'border-collapse': 'separate',
	},
	[CSS.row]: {
		display: 'block',
		'align-items': 'center',
		width: '100%',
	},
	[CSS.label]: {
		display: 'inline-block',
		width: '20%',
	},
	[CSS.value]: {
		display: 'inline-block',
		width: '70%',
	},
	[CSS.textfield]: {
		display: 'inline-block',
		width: '300px',
	},
});

const GPS_FIX_T_KEYS: { [fix in LocationDataRaw['fix']]?: T } = {
	gps: T.FIELD_LOCATION_FIX_GPS,
	dgps: T.FIELD_LOCATION_FIX_DGPS,
	pps: T.FIELD_LOCATION_FIX_PPS,
	rtkfloat: T.FIELD_LOCATION_FIX_RTKFLOAT,
	rtkfixed: T.FIELD_LOCATION_FIX_RTKFIXED,
};

class CollectionFieldViewLocation implements CollectionFieldView<IkeModel.CollectionModelFieldLocation> {
	viewInline(field: IkeModel.CollectionModelFieldLocation): MithrilVNode {
		if (field.hasValue()) {
			let lines = [];
			lines.push(this.locationFieldLine(field.value.latitude, T.LAT, 7));
			lines.push(this.locationFieldLine(field.value.longitude, T.LNG, 7));
			if (field.value.altitude !== null) {
				lines.push(this.altitudeFieldLine(field.value.altitude, T.FIELD_LOCATION_ALT, 2));
			}
			return m('div', { className: CSS.container }, m('table', { className: CSS.table }, lines));
		}

		return null;
	}

	private locationFieldLine(value: number, label: T, precision: number): MithrilVNode {
		return m('tr', { className: CSS.row }, [
			m('td', { className: CSS.label }, Translate(label)),
			m('td', { className: CSS.value }, [m('div', { className: CSS.textfield }, IkeUnit.toFixed(value, precision))]),
		]);
	}

	private altitudeFieldLine(value: number, label: T, precision: number): MithrilVNode {
		return m('tr', { className: CSS.row }, [
			m('td', { className: CSS.label }, Translate(label)),
			m('td', { className: CSS.value }, [
				m('div', { className: CSS.textfield }, IkeUnit.display(value, Services.UserSettings.units)),
			]),
		]);
	}

	hasDetail(field: IkeModel.CollectionModelFieldLocation): boolean {
		return true;
	}

	viewDetail(field: IkeModel.CollectionModelFieldLocation): MithrilVNode {
		if (!this.hasDetail(field)) {
			return null;
		}

		const raw = field.raw;
		const children = this.viewBasicDetail(field);

		if (raw.fix != null) {
			const translationKey = Translate(GPS_FIX_T_KEYS[raw.fix] || `"${raw.fix}"`);
			children.push(FieldInfo.viewStat(T.FIELD_LOCATION_FIX, translationKey));
		}

		// Only show accuracy for non manual locations, if they have the token
		if (
			(Services.Token.has(IkeToken.FEATURE_COLLECTION_LOCATION_ACCURACY) ||
				Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE)) &&
			!field.isManual() &&
			!field.isEdited()
		) {
			children.push(FieldInfo.viewStat(T.FIELD_LOCATION_ACCURACY, field.value.accuracy));
		}

		return m.fragment({}, children);
	}

	viewBasicDetail(field: IkeModel.CollectionModelFieldLocation) {
		const raw = field.raw;

		let res = [];

		//We want to display the provider first
		res.push(this.viewProvider(field));

		// Location has a ground altitude so show more information
		if ((field.isGPS() || field.isTrueSize()) && raw.groundAltitude != null) {
			res.push(FieldInfo.viewStat(T.FIELD_LOCATION_ALT_IKE, raw.deviceAltitude));
			res.push(FieldInfo.viewStat(T.FIELD_LOCATION_ALT_GROUND, raw.groundAltitude));
			res.push(FieldInfo.viewStat(T.FIELD_LOCATION_ANTENNA_HEIGHT, raw.antennaHeight));
		}

		return res;
	}

	viewProvider(field: IkeModel.CollectionModelFieldLocation): MithrilVNode {
		if (field.isManual()) {
			return FieldInfo.viewStat(T.FIELD_PROVIDER, Translate(T.FIELD_LOCATION_MANUAL));
		}
		if (field.isEdited()) {
			return FieldInfo.viewStat(T.FIELD_PROVIDER, Translate(T.FIELD_LOCATION_EDITED));
		}
		if (field.isTrueSize()) {
			return FieldInfo.viewStat(T.FIELD_PROVIDER, Translate(T.FIELD_LOCATION_TRUESIZE));
		}
		if (field.isGPS()) {
			return FieldInfo.viewStat(T.FIELD_PROVIDER, Translate(T.FIELD_LOCATION_GPS));
		}
		return FieldInfo.viewStat(T.FIELD_PROVIDER, field.provider);
	}

	viewEdit(field: IkeModel.CollectionModelFieldLocation, ctrl: CollectionEditCtrl): MithrilVNode | string {
		if (this.canUpdate()) {
			let inputState = ctrl.changeState.getState(field.id) as LocationInputState;
			if (inputState == null) {
				inputState = new LocationInputState(field.schema.displayName);
				inputState.onValue(() => {
					if (!LocationInputState.equal(field.value, inputState.value)) {
						field.Mutator.edited(inputState.value);
					}
				});
				inputState.setValue(field.value);
				ctrl.changeState.addState(field.id, inputState);
			}
			return m.fragment({}, [LocationInput.view(inputState)]);
		} else {
			return this.viewInline(field);
		}
	}

	hasClear(field: IkeModel.CollectionModelFieldLocation): boolean {
		return false;
	}

	canUpdate() {
		if (!Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE)) {
			return false;
		}
		return true;
	}
}

export const LocationFieldView = new CollectionFieldViewLocation();
